import { useCallback, useEffect, useState } from 'react';
import {
    Card, CardContent, CardDescription, CardHeader, CardTitle,
} from '../components/card';
import { Button } from '../components/button';
import { Input } from '../components/input';
import { Label } from '../components/label';
import { Badge } from '../components/badge';
import {
    Dialog, DialogContent, DialogDescription, DialogFooter,
    DialogHeader, DialogTitle, DialogTrigger,
} from '../components/dialog';
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/table';
import { Mail, Pencil, Phone, Plus, Trash2, Users as UsersIcon, BarChart3 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { User } from '../models/User';
import { Team } from '../models/Team';
import { TeamService } from '../services/TeamService';
import { UserService } from '../services/UserService';
import { toast } from 'sonner';

interface TeamsProps {
    user: User;
}

export function Teams({ user }: TeamsProps) {
    const [teams, setTeams] = useState<Team[]>([]);
    const [teamMembers, setTeamMembers] = useState<{ [teamId: number]: User[] }>({});
    const [isAddTeamOpen, setIsAddTeamOpen] = useState(false);
    const [isAddMemberOpen, setIsAddMemberOpen] = useState(false);
    const [selectedTeamId, setSelectedTeamId] = useState<number | null>(null);
    const [newTeamName, setNewTeamName] = useState('');
    const [memberEmail, setMemberEmail] = useState('');
    const [loading, setLoading] = useState(false);

    const loadTeams = useCallback(async () => {
        try {
            setLoading(true);
            const all = await TeamService.getTeamsByManagerId(user.id);
            setTeams(all);

            const membersMap: { [teamId: number]: User[] } = {};
            for (const team of all) {
                membersMap[team.id] = await UserService.getUsersByIds(team.userIds || []);
            }
            setTeamMembers(membersMap);
        } catch (e) {
            toast.error('Erreur lors du chargement des équipes');
        } finally {
            setLoading(false);
        }
    }, [user.id]);

    useEffect(() => {
        loadTeams();
    }, [loadTeams]);

    const handleCreateTeam = async () => {
        if (!newTeamName.trim()) return;
        try {
            await TeamService.createTeam({ name: newTeamName, managerId: user.id });
            await loadTeams();
            toast.success('Équipe créée avec succès');
            setNewTeamName('');
            setIsAddTeamOpen(false);
        } catch {
            toast.error('Erreur lors de la création de l\'équipe');
        }
    };

    const handleDeleteTeam = async (teamId: number) => {
        try {
            await TeamService.deleteTeam(teamId);
            await loadTeams();
            toast.success('Équipe supprimée');
        } catch {
            toast.error('Erreur lors de la suppression de l\'équipe');
        }
    };

    const handleAddMember = async () => {
        if (!selectedTeamId || !memberEmail.trim()) {
            toast.error('Veuillez saisir un ID utilisateur valide');
            return;
        }

        try {
            await TeamService.addUserToTeam(selectedTeamId, memberEmail);
            await loadTeams();
            toast.success('Membre ajouté avec succès');
        } catch {
            toast.error('Erreur lors de l\'ajout du membre');
        } finally {
            setIsAddMemberOpen(false);
            setSelectedTeamId(null);
            setMemberEmail('');
        }
    };

    const handleRemoveMember = async (teamId: number, memberId: number) => {
        try {
            await TeamService.removeUserFromTeam(teamId, memberId);
            await loadTeams();
            toast.success('Membre supprimé de l\'équipe');
        } catch {
            toast.error('Erreur lors de la suppression du membre');
        }
    };

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1>Gestion des équipes</h1>
                    <p className="text-muted-foreground">Gérez vos équipes et leurs membres</p>
                </div>

                {/* Bouton Créer équipe */}
                <Dialog open={isAddTeamOpen} onOpenChange={setIsAddTeamOpen}>
                    <DialogTrigger asChild>
                        <Button className="gap-2">
                            <Plus className="h-4 w-4" /> Nouvelle équipe
                        </Button>
                    </DialogTrigger>
                    <DialogContent>
                        <DialogHeader>
                            <DialogTitle>Créer une nouvelle équipe</DialogTitle>
                            <DialogDescription>Ajoutez une nouvelle équipe à votre organisation</DialogDescription>
                        </DialogHeader>
                        <div className="space-y-4 py-4">
                            <Label htmlFor="teamName">Nom de l'équipe</Label>
                            <Input
                                id="teamName"
                                placeholder="Ex: Équipe Marketing"
                                value={newTeamName}
                                onChange={(e) => setNewTeamName(e.target.value)}
                            />
                        </div>
                        <DialogFooter>
                            <Button variant="outline" onClick={() => setIsAddTeamOpen(false)}>Annuler</Button>
                            <Button onClick={handleCreateTeam}>Créer l'équipe</Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            </div>

            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                {teams.map((team) => {
                    const members = teamMembers[team.id] || [];
                    return (
                        <Card key={team.id}>
                            <CardHeader>
                                <div className="flex items-start justify-between">
                                    <div className="flex items-center gap-2">
                                        <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
                                            <UsersIcon className="h-5 w-5 text-primary" />
                                        </div>
                                        <div>
                                            <CardTitle>{team.name}</CardTitle>
                                            <CardDescription>
                                                {members.length} membre{members.length > 1 ? 's' : ''}
                                            </CardDescription>
                                        </div>
                                    </div>

                                    <div className="flex gap-1 pl-2">
                                        {/* Voir rapport */}
                                        <Button variant="ghost" size="sm" asChild>
                                            <Link to={`/report/${team.id}`}> 
                                                <BarChart3 className="h-4 w-4" />
                                            </Link>
                                        </Button>
                                
                                        {/* Modifier */}
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={async () => {
                                                const newName = window.prompt('Nom de l\'équipe', team.name);
                                                if (!newName?.trim() || newName.trim() === team.name) return;
                                                try {
                                                    await TeamService.updateTeam(team.id, { name: newName.trim() });
                                                    await loadTeams();
                                                    toast.success('Équipe mise à jour');
                                                } catch {
                                                    toast.error('Erreur lors de la mise à jour');
                                                        if (loading) {
                                                            return (
                                                                <div className="min-h-screen flex items-center justify-center">
                                                                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
                                                                </div>
                                                            );
                                                        }
                                                }
                                            }}
                                        >
                                            <Pencil className="h-4 w-4" />
                                        </Button>

                                        {/* Supprimer */}
                                        <Button variant="ghost" size="sm" onClick={() => handleDeleteTeam(team.id)}>
                                            <Trash2 className="h-4 w-4" />
                                        </Button>
                                    </div>
                                    </div>
                            </CardHeader>

                            <CardContent className="space-y-3">
                                {/* Membres */}
                                {members.slice(0, 3).map((member) => (
                                    <div key={member.id} className="flex items-center gap-2 text-sm">
                                        <div className="h-6 w-6 rounded-full bg-muted flex items-center justify-center text-xs">
                                            {member.firstName[0]}{member.lastName[0]}
                                        </div>
                                        <span>{member.firstName} {member.lastName}</span>
                                    </div>
                                ))}
                                {members.length > 3 && (
                                    <p className="text-muted-foreground text-sm">
                                        +{members.length - 3} autre{members.length - 3 > 1 ? 's' : ''}
                                    </p>
                                )}

                                <Dialog
                                    open={isAddMemberOpen && selectedTeamId === team.id}
                                    onOpenChange={(open) => {
                                        setIsAddMemberOpen(open);
                                        if (open) setSelectedTeamId(team.id);
                                        else setSelectedTeamId(null);
                                    }}
                                >
                                    <DialogTrigger asChild>
                                        <Button variant="outline" size="sm" className="w-full gap-2">
                                            <Plus className="h-4 w-4" /> Ajouter un membre
                                        </Button>
                                    </DialogTrigger>
                                    <DialogContent>
                                        <DialogHeader>
                                            <DialogTitle>Ajouter un membre à {team.name}</DialogTitle>
                                            <DialogDescription>
                                                Entrez l'identifiant de l'utilisateur à ajouter.
                                            </DialogDescription>
                                        </DialogHeader>
                                        <div className="py-4 space-y-2">
                                            <Label htmlFor="userId">Email de l'utilisateur</Label>
                                            <Input
                                                id="userEmail"
                                                placeholder="Ex: john.doe@epitime.com"
                                                value={memberEmail}
                                                onChange={(e) => setMemberEmail(e.target.value)}
                                            />
                                        </div>
                                        <DialogFooter>
                                            <Button variant="outline" onClick={() => setIsAddMemberOpen(false)}>Annuler</Button>
                                            <Button onClick={handleAddMember}>Ajouter</Button>
                                        </DialogFooter>
                                    </DialogContent>
                                </Dialog>
                            </CardContent>
                        </Card>
                    );
                })}
            </div>

            <Card>
                <CardHeader>
                    <CardTitle>Tous les membres</CardTitle>
                    <CardDescription>Vue détaillée de tous les employés de vos équipes</CardDescription>
                </CardHeader>
                <CardContent>
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Employé</TableHead>
                                <TableHead>Équipe</TableHead>
                                <TableHead>Email</TableHead>
                                <TableHead>Téléphone</TableHead>
                                <TableHead>Statut</TableHead>
                                <TableHead className="text-right">Actions</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {teams.flatMap((team) =>
                                (teamMembers[team.id] || []).map((member) => (
                                    <TableRow key={`${team.id}-${member.id}`}>
                                        <TableCell>
                                            <div className="flex items-center gap-3">
                                                <div className="h-8 w-8 rounded-full bg-muted flex items-center justify-center text-sm">
                                                    {member.firstName[0]}{member.lastName[0]}
                                                </div>
                                                <div>
                                                    <p>{member.firstName} {member.lastName}</p>
                                                    <p className="text-muted-foreground capitalize">{member.role}</p>
                                                </div>
                                            </div>
                                        </TableCell>
                                        <TableCell>{team.name}</TableCell>
                                        <TableCell>
                                            <div className="flex items-center gap-2">
                                                <Mail className="h-4 w-4 text-muted-foreground" />
                                                {member.email}
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            <div className="flex items-center gap-2">
                                                <Phone className="h-4 w-4 text-muted-foreground" />
                                                {member.phoneNumber}
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            <Badge variant="outline">Actif</Badge>
                                        </TableCell>
                                        <TableCell className="text-right">
                                            <Button variant="ghost" size="sm" onClick={() => handleRemoveMember(team.id, member.id)}>
                                                <Trash2 className="h-4 w-4" />
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </CardContent>
            </Card>
        </div>
    );
}
